/**
 * 审批流程图（只读）。给定部署 ID 或直接给 XML，用 bpmn-js Viewer 渲染，不依赖 iframe 子应用。
 * 节点状态由宿主以 nodes 传入，在 SVG 图层叠加节点卡片与起止图标。
 */
<template>
  <div ref="container" class="approval-bpmn" :style="containerStyle">
    <div ref="canvas" class="approval-bpmn__canvas" :style="containerStyle"></div>
    <div v-if="errorMsg" class="approval-bpmn__error">{{ errorMsg }}</div>
  </div>
</template>

<script>
import BpmnViewer from 'bpmn-js/lib/Viewer';
import camundaModdleDescriptor from 'camunda-bpmn-moddle/resources/camunda';
import serverConfig from '@/assets/global/serverConfig';

/** 缩放下限：低于该比例文字难以辨认，改为锁定比例 + 横向滚动 */
const MIN_FIT_SCALE = 0.6;
/** 放大查看的倍率上限与四周留白：弹框比页内宽，靠这一档宽度差异放大 */
const DETAIL_SCALE = 1.6;
const DETAIL_PADDING = 48;
/** 图上覆盖层的状态文案与样式类 */
const DIAGRAM_STATE_TEXT = { Y: '已通过', A: '审批中', R: '已驳回' };
const NODE_IMAGES = {
  Y: require('@/assets/images/approvalProcess/users.jpg'),
  A: require('@/assets/images/approvalProcess/users2.jpg'),
  R: require('@/assets/images/approvalProcess/users1.jpg'),
  none: require('@/assets/images/approvalProcess/users3.jpg')
};
const START_IMAGE = require('@/assets/images/approvalProcess/start.png');
const END_IMAGE = require('@/assets/images/approvalProcess/end.png');
const SVG_NS = 'http://www.w3.org/2000/svg';

/** 流程定义缺失时的兜底示意图，保证区块不空白 */
const DEFAULT_XML = `<?xml version="1.0" encoding="UTF-8"?>
<bpmn2:definitions xmlns:bpmn2="http://www.omg.org/spec/BPMN/20100524/MODEL" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xmlns:bpmndi="http://www.omg.org/spec/BPMN/20100524/DI" xmlns:dc="http://www.omg.org/spec/DD/20100524/DC" xmlns:di="http://www.omg.org/spec/DD/20100524/DI" id="sample-diagram" targetNamespace="http://bpmn.io/schema/bpmn" xsi:schemaLocation="http://www.omg.org/spec/BPMN/20100524/MODEL BPMN20.xsd">
  <bpmn2:process id="process1567044459787" name="流程1567044459787" isExecutable="true">
    <bpmn2:documentation>描述</bpmn2:documentation>
    <bpmn2:startEvent id="StartEvent_01ydzqe" name="开始">
      <bpmn2:outgoing>SequenceFlow_1qw929z</bpmn2:outgoing>
    </bpmn2:startEvent>
    <bpmn2:sequenceFlow id="SequenceFlow_1qw929z" sourceRef="StartEvent_01ydzqe" targetRef="Task_1piqdk6" />
    <bpmn2:userTask id="Task_1piqdk6" name="请假申请">
      <bpmn2:incoming>SequenceFlow_1qw929z</bpmn2:incoming>
      <bpmn2:outgoing>SequenceFlow_0axt6j6</bpmn2:outgoing>
    </bpmn2:userTask>
    <bpmn2:endEvent id="EndEvent_0pnmjd3">
      <bpmn2:incoming>SequenceFlow_0axt6j6</bpmn2:incoming>
    </bpmn2:endEvent>
    <bpmn2:sequenceFlow id="SequenceFlow_0axt6j6" sourceRef="Task_1piqdk6" targetRef="EndEvent_0pnmjd3" />
  </bpmn2:process>
  <bpmndi:BPMNDiagram id="BPMNDiagram_1">
    <bpmndi:BPMNPlane id="BPMNPlane_1" bpmnElement="process1567044459787">
      <bpmndi:BPMNShape id="StartEvent_01ydzqe_di" bpmnElement="StartEvent_01ydzqe">
        <dc:Bounds x="532" y="72" width="36" height="36" />
      </bpmndi:BPMNShape>
      <bpmndi:BPMNEdge id="SequenceFlow_1qw929z_di" bpmnElement="SequenceFlow_1qw929z">
        <di:waypoint x="550" y="108" />
        <di:waypoint x="550" y="150" />
      </bpmndi:BPMNEdge>
      <bpmndi:BPMNShape id="UserTask_1qxjy46_di" bpmnElement="Task_1piqdk6">
        <dc:Bounds x="500" y="150" width="100" height="80" />
      </bpmndi:BPMNShape>
      <bpmndi:BPMNShape id="EndEvent_0pnmjd3_di" bpmnElement="EndEvent_0pnmjd3">
        <dc:Bounds x="532" y="338" width="36" height="36" />
      </bpmndi:BPMNShape>
      <bpmndi:BPMNEdge id="SequenceFlow_0axt6j6_di" bpmnElement="SequenceFlow_0axt6j6">
        <di:waypoint x="550" y="230" />
        <di:waypoint x="550" y="338" />
      </bpmndi:BPMNEdge>
    </bpmndi:BPMNPlane>
  </bpmndi:BPMNDiagram>
</bpmn2:definitions>`;

export default {
  name: 'LookBpmn',
  props: {
    /** 流程部署 ID；为空时用 xml 或兜底示意图 */
    deploymentId: { type: String, default: '' },
    /** 流程实例 ID，实例级动态 XML 后续接入，本单不使用 */
    processInstanceId: { type: String, default: '' },
    /** 直接传入流程定义 XML（优先于接口拉取） */
    xml: { type: String, default: '' },
    /** 节点状态列表，按 activityId 与图元匹配；为空时按图上的节点名渲染未开始态 */
    nodes: { type: Array, default: () => [] },
    /** 画布高度（px） */
    height: { type: [Number, String], default: 300 },
    /** 放大查看模式：按容器宽度铺满、高度跟随内容，整图仍全部可见 */
    contentFit: { type: Boolean, default: false }
  },
  data() {
    return {
      processScript: '',
      errorMsg: '',
      diagramReady: false,
      /** 放大查看时按内容算出的画布高度 */
      autoHeight: 0
    };
  },
  computed: {
    containerStyle() {
      if (this.contentFit && this.autoHeight) return { height: `${this.autoHeight}px` };
      const value = typeof this.height === 'number' ? `${this.height}px` : String(this.height);
      return { height: value };
    }
  },
  watch: {
    nodes: {
      deep: true,
      handler() {
        this.drawOverlays();
      }
    },
    deploymentId(value) {
      if (this.xml || !value) return;
      this.loadByDeployment(value);
    },
    xml(value) {
      if (value) {
        this.render(value);
      } else if (this.deploymentId) {
        this.loadByDeployment(this.deploymentId);
      } else {
        this.render(DEFAULT_XML);
      }
    }
  },
  mounted() {
    this.viewer = new BpmnViewer({
      container: this.$refs.canvas,
      moddleExtensions: { camunda: camundaModdleDescriptor }
    });
    this.observeResize();
    if (this.xml) {
      this.render(this.xml);
    } else if (this.deploymentId) {
      this.loadByDeployment(this.deploymentId);
    } else {
      this.render(DEFAULT_XML);
    }
  },
  beforeDestroy() {
    this.dispose();
  },
  methods: {
    /** 按部署 ID 拉取流程定义；失败或缺失时回退兜底示意图 */
    loadByDeployment(deploymentId) {
      this.errorMsg = '';
      if (!this.api || typeof this.api.get !== 'function') {
        this.render(DEFAULT_XML);
        return;
      }
      this.api
        .get(`/camunda-server/process-definition/get-detail?deploymentId=${encodeURIComponent(deploymentId)}`)
        .then((res) => {
          const body = res && res.data ? res.data : {};
          if (body.code !== serverConfig.REQUIRE_SUCCESS_CODE) {
            this.fallback(body.msg || '流程定义加载失败');
            return;
          }
          const script = body.data && body.data.processScript;
          this.render(script || DEFAULT_XML);
        })
        .catch(() => {
          this.fallback('流程定义加载失败');
        });
    },
    /** 记下错误提示并回退兜底示意图，保证区块不空白 */
    fallback(message) {
      this.errorMsg = message;
      this.render(DEFAULT_XML);
    },
    /** 渲染指定 XML；成功后做自适应并标记箭头样式 */
    render(xml) {
      if (!this.viewer || !xml) return;
      this.processScript = xml;
      this.diagramReady = false;
      this.viewer.importXML(xml, (err) => {
        if (err) {
          this.errorMsg = '流程图渲染失败';
          // 传入的 XML 本身有问题时退到兜底图，避免画布留白
          if (xml !== DEFAULT_XML) this.render(DEFAULT_XML);
          return;
        }
        this.diagramReady = true;
        this.decorateConnections();
        this.drawOverlays();
        this.fitView();
        this.$emit('rendered', this.viewer);
      });
    },
    /** 统一连线颜色并替换箭头，和现网 iframe 的观感保持一致 */
    decorateConnections() {
      const root = this.$refs.canvas;
      if (!root) return;
      const defs = root.querySelector('svg defs');
      if (!defs) return;
      const markerId = `approval-bpmn-arrow-${this._uid}`;
      if (!defs.querySelector(`#${markerId}`)) {
        const marker = document.createElementNS(SVG_NS, 'marker');
        marker.setAttribute('id', markerId);
        marker.setAttribute('viewBox', '0 0 20 20');
        marker.setAttribute('refX', '11');
        marker.setAttribute('refY', '10');
        marker.setAttribute('markerWidth', '10');
        marker.setAttribute('markerHeight', '10');
        marker.setAttribute('orient', 'auto');
        const path = document.createElementNS(SVG_NS, 'path');
        path.setAttribute('d', 'M 1 5 L 11 10 L 1 15 Z');
        path.setAttribute('style', 'stroke-width:1px;stroke-linecap:round;stroke:#A8A8A8;fill:#A8A8A8;');
        marker.appendChild(path);
        defs.appendChild(marker);
      }
      const connections = root.querySelectorAll('.djs-connection path');
      for (let i = 0; i < connections.length; i += 1) {
        connections[i].style.stroke = '#A8A8A8';
        connections[i].style.markerEnd = `url(#${markerId})`;
      }
    },
    /** 按节点状态在 SVG 图层重绘：起止图标 + 节点四态（名称 / 处理人 / 状态） */
    drawOverlays() {
      if (!this.viewer || !this.diagramReady) return;
      this.hideNativeLabels();
      const layer = this.viewer.get('canvas').getLayer('approval-overlay');
      while (layer.firstChild) layer.removeChild(layer.firstChild);
      const stateMap = new Map((this.nodes || []).map((node) => [node.activityId, node]));
      const registry = this.viewer.get('elementRegistry');
      registry.filter((item) => item.type === 'bpmn:StartEvent').forEach((item) => this.appendIcon(layer, item, 'start'));
      registry.filter((item) => item.type === 'bpmn:EndEvent').forEach((item) => this.appendIcon(layer, item, 'end'));
      registry
        .filter((item) => item.type === 'bpmn:UserTask' || item.type === 'bpmn:ServiceTask')
        .forEach((shape) => this.appendNode(layer, shape, stateMap.get(shape.id) || {}));
    },
    /**
     * 隐藏 BPMN 自带标签：文案已由覆盖层接管（业务节点画三行字、起止图标自带文字）。
     * 起止节点的标签是独立的元素（不在图元内部），只能按 DOM 隐藏。
     */
    hideNativeLabels() {
      const root = this.$refs.canvas;
      if (!root) return;
      const labels = root.querySelectorAll('.djs-label');
      for (let i = 0; i < labels.length; i += 1) labels[i].style.display = 'none';
    },
    /** 起止节点图标，尺寸与现网一致 */
    appendIcon(layer, element, kind) {
      const image = document.createElementNS(SVG_NS, 'image');
      image.setAttribute('x', element.x - 6);
      image.setAttribute('y', element.y - 6);
      image.setAttribute('width', 48);
      image.setAttribute('height', 48);
      image.setAttribute('href', kind === 'start' ? START_IMAGE : END_IMAGE);
      layer.appendChild(image);
    },
    /** 节点卡片：底图按状态选择，文字取表格数据，缺失时回退图上的配置 */
    appendNode(layer, shape, node) {
      this.viewer.get('canvas').addMarker(shape.id, 'approval-node');
      const business = shape.businessObject || {};
      const attrs = business.$attrs || {};
      const state = node.approvalState || '';
      const name = node.processName || business.name || '';
      // 发起人节点沿用流程定义中的配置范围，审批操作表仍显示实际人员。
      const staffName = name === '发起人' ? attrs['camunda:nodeName'] || '' : node.staffName || attrs['camunda:nodeName'] || '';
      const group = document.createElementNS(SVG_NS, 'g');
      const image = document.createElementNS(SVG_NS, 'image');
      image.setAttribute('x', shape.x - 1.5);
      image.setAttribute('y', shape.y - 1.5);
      image.setAttribute('width', shape.width + 3);
      image.setAttribute('height', shape.height + 3);
      // 底图按拉伸铺满：默认按比例居中缩放会比图元矮一截，
      // 流程自身的边框（部分节点是黑框）就会从卡片上下各露出 1px
      image.setAttribute('preserveAspectRatio', 'none');
      image.setAttribute('href', NODE_IMAGES[state] || (name === '发起人' ? NODE_IMAGES.Y : NODE_IMAGES.none));
      group.appendChild(image);
      layer.appendChild(group);
      this.appendText(group, shape, name, 'name', 22, 0.2);
      this.appendText(group, shape, staffName, 'staff', 10, 0.58);
      this.appendText(group, shape, DIAGRAM_STATE_TEXT[state] || '', `state is-${(state || 'none').toLowerCase()}`, 10, 0.78);
    },
    /** 单行文字，超长截断，避免压出节点卡片 */
    appendText(parent, shape, text, className, offsetX, ratioY) {
      const content = String(text || '');
      const label = document.createElementNS(SVG_NS, 'text');
      label.setAttribute('x', shape.x + offsetX);
      label.setAttribute('y', shape.y + shape.height * ratioY);
      label.setAttribute('class', `approval-overlay-text ${className}`);
      if (className === 'name') label.setAttribute('dominant-baseline', 'central');
      const characters = Array.from(content);
      const visible = characters.slice(0, 8);
      const displayText = () => visible.join('') + (visible.length < characters.length ? '…' : '');
      label.textContent = displayText();
      parent.appendChild(label);
      const maxWidth = Math.max(0, shape.width - offsetX - 4);
      while (visible.length && typeof label.getComputedTextLength === 'function' && label.getComputedTextLength() > maxWidth) {
        visible.pop();
        label.textContent = displayText();
      }
      const tip = document.createElementNS(SVG_NS, 'title');
      tip.textContent = content;
      label.appendChild(tip);
    },
    /**
     * 自适应缩放：整图缩放到可见；比例低于下限时锁定下限并撑宽画布，
     * 由外层容器出横向滚动条（不裁切、不整体右移）。
     */
    fitView() {
      if (!this.viewer) return;
      const root = this.$refs.canvas;
      if (!root || root.clientWidth === 0) return;
      const canvas = this.viewer.get('canvas');
      root.style.minWidth = '';
      canvas.resized();
      if (this.contentFit) {
        this.fitContent(root, canvas);
        return;
      }
      const fitScale = canvas.zoom('fit-viewport', 'auto');
      if (fitScale >= MIN_FIT_SCALE) return;
      root.style.minWidth = `${Math.ceil(canvas.viewbox().inner.width * MIN_FIT_SCALE + 60)}px`;
      canvas.resized();
      const bounds = canvas.viewbox().inner;
      const width = root.clientWidth / MIN_FIT_SCALE;
      const height = root.clientHeight / MIN_FIT_SCALE;
      canvas.viewbox({
        x: bounds.x - 30 / MIN_FIT_SCALE,
        y: bounds.y - (height - bounds.height) / 2,
        width,
        height
      });
    },
    /**
     * 放大查看：按容器宽度铺满（不裁掉首尾节点），高度贴合内容。
     * 不用 canvas.zoom('fit-viewport')：它自身封顶 1:1，不会放大；
     * 弹框比页内宽，靠这一档宽度差实现放大。
     */
    fitContent(root, canvas) {
      const inner = canvas.viewbox().inner;
      const usableWidth = Math.max(root.clientWidth - DETAIL_PADDING, 1);
      const scale = Math.min(DETAIL_SCALE, usableWidth / inner.width);
      root.style.minWidth = '';
      const height = Math.ceil(inner.height * scale + DETAIL_PADDING);
      if (height !== this.autoHeight) {
        this.autoHeight = height;
        this.$nextTick(() => this.applyContentZoom(canvas, scale, inner));
        return;
      }
      this.applyContentZoom(canvas, scale, inner);
    },
    /** 画布尺寸落定后按内容居中摆放，避免首尾节点被裁在可视区外 */
    applyContentZoom(canvas, scale, inner) {
      if (!this.viewer) return;
      const root = this.$refs.canvas;
      canvas.resized();
      const width = root.clientWidth / scale;
      const height = root.clientHeight / scale;
      canvas.viewbox({
        x: inner.x - (width - inner.width) / 2,
        y: inner.y - (height - inner.height) / 2,
        width,
        height
      });
    },
    /** 容器尺寸变化时重算；监听外层容器，避免改 canvas 宽度触发自激 */
    observeResize() {
      if (typeof ResizeObserver !== 'undefined' && this.$refs.container) {
        this.resizeObserver = new ResizeObserver(() => {
          if (!this.diagramReady) return;
          this.fitView();
        });
        this.resizeObserver.observe(this.$refs.container);
        return;
      }
      // 无 ResizeObserver 的旧环境退化为窗口尺寸变化
      this.windowResizeHandler = () => {
        if (!this.diagramReady) return;
        this.fitView();
      };
      window.addEventListener('resize', this.windowResizeHandler);
    },
    /** 释放图实例与尺寸监听，避免反复进出详情页残留 */
    dispose() {
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      }
      if (this.windowResizeHandler) {
        window.removeEventListener('resize', this.windowResizeHandler);
        this.windowResizeHandler = null;
      }
      if (this.viewer) {
        try {
          this.viewer.destroy();
        } catch (e) {
          // 已销毁时忽略
        }
        this.viewer = null;
      }
      this.diagramReady = false;
    },
    /** 供宿主页面在容器由隐藏变为可见后手动重算 */
    refresh() {
      this.fitView();
    }
  }
};
</script>

<style lang="less">
@import '~bpmn-js/dist/assets/diagram-js.css';
</style>

<style lang="less" scoped>
.approval-bpmn {
  position: relative;
  width: 100%;
  background: #e9eef3;
  overflow: auto;

  &__canvas {
    width: 100%;
  }

  &__error {
    position: absolute;
    left: 12px;
    bottom: 8px;
    font-size: 12px;
    color: #fe4638;
    background: rgba(255, 255, 255, 0.85);
    padding: 2px 6px;
    border-radius: 2px;
  }

  ::v-deep .bjs-powered-by {
    display: none;
  }

  ::v-deep .djs-palette {
    display: none;
  }

  ::v-deep .djs-container {
    background: transparent;
  }

  ::v-deep .approval-overlay-text {
    fill: #ffffff;
    font-size: 12px;
    pointer-events: none;

    &.staff {
      fill: #323233;
      pointer-events: auto;
    }

    &.state.is-y {
      fill: #4fa1a4;
    }

    &.state.is-a {
      fill: #ff8623;
    }

    &.state.is-r {
      fill: #fe4638;
    }
  }

}
</style>
